import * as React from 'react';
import { addMinutes } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import {
  CircularProgress,
  Grid2,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { ServiceCategory } from '@prisma/client';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Month from './Days/Month';
import CalendarHeader from './CalendarHeader';
import CalendarNav from './CalendarNav';
import TimeList from './Times/TimeList';
import './calendar.css';
import CustomDate from './Times/CustomDate';
import useEmployeeAvailableDays from '../../hooks/appointments/useEmployeeAvailableDays';
import TextInput from '../form/TextInput';
import { TakenSessionSlot } from '../../types/appointments';

type Props = {
  label?: string;
  name?: string;
  employeeId: string;
  appointmentLengthInMinutes: string;
  startDateTime: string | undefined;
  rescheduleAppointmentId?: string;
  takenSlots?: TakenSessionSlot[];
  onChange: (value: Date) => void;
  onDelete?: () => void;
  disabled: boolean;
  timezone: string;
  serviceCategory?: ServiceCategory;
  error?: string;
};

export default function Calendar({
  label,
  name,
  employeeId,
  appointmentLengthInMinutes,
  startDateTime,
  rescheduleAppointmentId,
  takenSlots,
  onChange,
  onDelete,
  disabled,
  timezone,
  serviceCategory,
  error,
}: Props) {
  const isDesktop = useMediaQuery('(min-width:900px)');
  const [usersTime, setUsersTime] = React.useState(
    formatInTimeZone(new Date(), timezone, 'h:mm a'),
  );
  const [selectedDay, setSelectedDay] = React.useState(new Date());
  const [dayIsSelected, setDayIsSelected] = React.useState(false);
  const [formattedDateString, setFormattedDateString] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [dateError, setDateError] = React.useState('');
  const handleOpen = () => {
    if (appointmentLengthInMinutes && employeeId && !disabled) {
      setDateError('');
      setOpen(true);
    } else {
      setDateError(
        'Please select an artist and length of time to view calendar options',
      );
    }
  };
  const handleClose = () => setOpen(false);

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: isDesktop ? 'auto' : '100%',
    bgcolor: 'background.paper',
  };
  React.useEffect(() => {
    if (startDateTime) {
      const newString = formatInTimeZone(
        new Date(startDateTime),
        timezone,
        'MMMM do, yyyy, h:mma',
      );
      let endString = '';
      if (appointmentLengthInMinutes) {
        const endDateTime = addMinutes(
          new Date(startDateTime),
          parseInt(appointmentLengthInMinutes, 10),
        );
        endString = formatInTimeZone(endDateTime, timezone, 'h:mma z');
      }

      setFormattedDateString(`${newString} - ${endString}`);
    } else setFormattedDateString('');
  }, [appointmentLengthInMinutes, timezone, startDateTime]);

  React.useEffect(() => {
    const interval = setInterval(() => {
      const date = new Date();
      const userTimeString = formatInTimeZone(date, timezone, 'h:mm a');
      setUsersTime(userTimeString);
    }, 30000);

    return () => clearInterval(interval);
  }, [timezone]);

  const {
    query: {
      data: availableDays,
      isFetching,
      isError,
      isLoading: isLoadingInitial,
    },
    state: { initialCurrentMonth, selectedMonth, setSelectedMonth },
    noAvailableDays,
  } = useEmployeeAvailableDays({
    duration: appointmentLengthInMinutes,
    employeeId,
    rescheduleAppointmentId,
    serviceCategory,
    takenSlots,
  });

  const loading = isFetching;
  const uiMonth = selectedMonth || initialCurrentMonth;

  return (
    <>
      <button
        onClick={handleOpen}
        style={{
          display: 'block',
          background: 'transparent',
          border: 'none',
          width: '100%',
          paddingLeft: 0,
          paddingRight: 0,
          marginTop: -1,
          marginBottom: -1,
        }}
        type="button">
        <TextInput
          type="text"
          name={name || 'dateTime'}
          label={label || 'Date & Time *'}
          value={formattedDateString}
          disabled={disabled}
          placeholder="Select a date and time"
          isError={!!dateError || !!error}
          endAdornment={
            onDelete
              ? {
                  icon: CancelOutlinedIcon,
                  onClick: onDelete,
                }
              : undefined
          }
          readOnly
        />
      </button>
      {dateError && (
        <Box className="error-message" my={0.5}>
          <Typography variant="body1" color="primary">
            {dateError}
          </Typography>
        </Box>
      )}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={style}>
          <div
            className="schedule-box"
            style={{
              width: dayIsSelected ? 540 : 360,
            }}>
            <Grid2 container spacing={1}>
              <Grid2
                size={{
                  xs: 12,
                  sm: dayIsSelected ? 8 : 12,
                }}
                className={`calendar-box ${dayIsSelected ? 'hidden' : ''}`}>
                <CalendarNav
                  selectedMonth={uiMonth}
                  setSelectedMonth={setSelectedMonth}
                  hideButtons={!!noAvailableDays || isLoadingInitial}
                />
                <CalendarHeader />

                {loading && (
                  <Box pt={3}>
                    <CircularProgress
                      color="primary"
                      style={{ margin: 'auto', display: 'block' }}
                    />
                  </Box>
                )}

                {!loading && (
                  <>
                    {noAvailableDays && (
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography variant="body2" paddingY={1}>
                          No available times found for this appointment
                        </Typography>
                        <CustomDate onChange={onChange} />
                      </Box>
                    )}

                    {!noAvailableDays && (
                      <>
                        <Month
                          dayItems={availableDays}
                          selectedMonth={uiMonth}
                          setSelectedDay={setSelectedDay}
                          setDayIsSelected={setDayIsSelected}
                        />
                        <div className="timezone-box-desktop">
                          <Typography variant="h3" className="timezone-header">
                            Time zone
                          </Typography>
                          <Typography variant="body2">
                            {timezone} {usersTime}
                          </Typography>
                        </div>
                      </>
                    )}
                  </>
                )}

                {isError && !availableDays?.length && !noAvailableDays ? (
                  <>
                    <Typography variant="body2" paddingY={1}>
                      Error loading calendar for this artist. Please refresh the
                      page and try again
                    </Typography>
                    <CustomDate onChange={onChange} />
                  </>
                ) : null}
              </Grid2>
              {dayIsSelected ? (
                <TimeList
                  selectedDay={selectedDay}
                  duration={appointmentLengthInMinutes}
                  employeeId={employeeId}
                  timezone={timezone}
                  onChange={onChange}
                  closeModal={handleClose}
                  setDayIsSelected={setDayIsSelected}
                  serviceCategory={serviceCategory}
                  takenSlots={takenSlots}
                />
              ) : null}
            </Grid2>
          </div>
        </Box>
      </Modal>
    </>
  );
}
